import {ActionList, ActionMenu, Button} from '@primer/react'
import {AgentIcon, DeviceDesktopIcon, PlusIcon, ServerIcon, TriangleDownIcon} from '@primer/octicons-react'
import {useEffect, useMemo, useRef, useState} from 'react'

import {ConnectDeviceDialog} from './ConnectDeviceDialog'
import {DirectoryPicker} from './DirectoryPicker'
import type {EnvironmentOption} from './environment-option'
import {useConnectedDevices, type ConnectedDevice} from '../../../hooks/useConnectedDevices'
import {useEnvironments} from '../../../hooks/use-environments'
import type {Environment} from '../../../types/environment'
import styles from './EnvironmentPicker.module.css'

const CLOUD_ENV: EnvironmentOption = {id: 'background-agent', kind: 'cloud', label: 'Cloud', icon: AgentIcon}

const DEFAULT_ENVIRONMENTS: EnvironmentOption[] = [CLOUD_ENV]

const DEVICE_OPTION_PREFIX = 'local-device:'

const deviceOptionId = (deviceId: string, path: string) => `${DEVICE_OPTION_PREFIX}${deviceId}::${path}`

const AHP_OPTION_PREFIX = 'ahp-environment:'

const buildAhpOptions = (environments: Environment[]): EnvironmentOption[] =>
  environments
    .filter(env => env.status === 'online' && !!env.capabilities?.ahp_version)
    .map(env => ({
      id: `${AHP_OPTION_PREFIX}${env.id}`,
      kind: 'ahp' as const,
      label: env.name,
      icon: ServerIcon,
      environmentId: env.id,
    }))

const buildLocalOptions = (devices: ConnectedDevice[]): EnvironmentOption[] =>
  devices.flatMap(d =>
    d.directories.map(dir => ({
      id: deviceOptionId(d.id, dir.path),
      kind: 'local' as const,
      label: d.displayName,
      icon: DeviceDesktopIcon,
      workingDirectory: dir.path,
      deviceId: d.id,
    })),
  )

type EnvironmentPickerProps = {
  /** Available environments to choose from. Grouped in the dropdown by `kind`,
   * and (for `local`) further grouped by `deviceId`. If omitted, defaults to
   * the built-in cloud environments plus the live list of connected local
   * devices fetched via `useConnectedDevices` (one option per device working
   * directory). */
  environments?: EnvironmentOption[]
  selectedEnvironmentId?: string
  onSelectEnvironment?: (environment: EnvironmentOption) => void
  /** Called when the currently selected environment is no longer in the available
   * list (e.g. a local device disconnected between selection and submit). */
  onSelectedEnvironmentUnavailable?: () => void
  onConnectDevice?: () => void
  disabled?: boolean
}

export function EnvironmentPicker({
  environments,
  selectedEnvironmentId,
  onSelectEnvironment,
  onSelectedEnvironmentUnavailable,
  onConnectDevice,
  disabled = false,
}: EnvironmentPickerProps) {
  const {data: fetchedDevices} = useConnectedDevices()
  const {data: ahpEnvironments} = useEnvironments({status: 'online'})
  const allEnvironments = useMemo<EnvironmentOption[]>(() => {
    if (environments) return environments
    return [
      ...DEFAULT_ENVIRONMENTS,
      ...buildAhpOptions(ahpEnvironments ?? []),
      ...buildLocalOptions(fetchedDevices ?? []),
    ]
  }, [environments, ahpEnvironments, fetchedDevices])

  const cloudEnvironments = useMemo(() => allEnvironments.filter(env => env.kind === 'cloud'), [allEnvironments])
  const ahpOptions = useMemo(() => allEnvironments.filter(env => env.kind === 'ahp'), [allEnvironments])
  const localOptions = useMemo(() => allEnvironments.filter(env => env.kind === 'local'), [allEnvironments])

  const [internalSelectedId, setInternalSelectedId] = useState<string>(
    () => selectedEnvironmentId ?? allEnvironments[0]?.id ?? CLOUD_ENV.id,
  )
  const [isConnectDeviceDialogOpen, setIsConnectDeviceDialogOpen] = useState(false)
  const activeId = selectedEnvironmentId ?? internalSelectedId
  const selected = allEnvironments.find(env => env.id === activeId) ?? allEnvironments[0]

  // The directory dropdown lives next to the device dropdown and is only
  // meaningful when a local device is the active environment. Group local
  // options by deviceId so we can render the directories for the selected
  // device in the secondary picker.
  const localOptionsByDevice = useMemo(() => {
    const groups = new Map<string, EnvironmentOption[]>()
    for (const opt of localOptions) {
      if (!opt.deviceId) continue
      const arr = groups.get(opt.deviceId) ?? []
      arr.push(opt)
      groups.set(opt.deviceId, arr)
    }
    return groups
  }, [localOptions])

  const selectedDeviceOptions =
    selected?.kind === 'local' && selected.deviceId ? (localOptionsByDevice.get(selected.deviceId) ?? []) : []

  // Notify parent if the controlled selection has disappeared from the available
  // list (e.g. the chosen local device disconnected) so it can reset its state
  // and avoid dispatching to a now-unavailable target.
  const onSelectedEnvironmentUnavailableRef = useRef(onSelectedEnvironmentUnavailable)
  useEffect(() => {
    onSelectedEnvironmentUnavailableRef.current = onSelectedEnvironmentUnavailable
  }, [onSelectedEnvironmentUnavailable])
  const isSelectedUnavailable =
    !!selectedEnvironmentId && !allEnvironments.some(env => env.id === selectedEnvironmentId)
  useEffect(() => {
    if (isSelectedUnavailable) {
      onSelectedEnvironmentUnavailableRef.current?.()
    }
  }, [isSelectedUnavailable])

  const handleSelect = (env: EnvironmentOption) => {
    setInternalSelectedId(env.id)
    onSelectEnvironment?.(env)
  }

  // When the user picks a device from the device dropdown, auto-select its
  // first directory so the submit button is actionable without forcing a
  // second click on devices that have a single directory.
  const handleSelectDevice = (deviceId: string) => {
    const firstOption = localOptionsByDevice.get(deviceId)?.[0]
    if (firstOption) handleSelect(firstOption)
  }

  const handleConnectDevice = () => {
    if (onConnectDevice) {
      onConnectDevice()
    } else {
      setIsConnectDeviceDialogOpen(true)
    }
  }

  // The device dropdown header renders one row per *device* (not per
  // directory). The active row is determined by the device of the
  // currently-selected option, so it stays visually consistent with the
  // directory picker beside it.
  const uniqueDevices: Array<{deviceId: string; label: string; icon: EnvironmentOption['icon']}> = useMemo(() => {
    const seen = new Set<string>()
    const out: Array<{deviceId: string; label: string; icon: EnvironmentOption['icon']}> = []
    for (const opt of localOptions) {
      if (!opt.deviceId || seen.has(opt.deviceId)) continue
      seen.add(opt.deviceId)
      out.push({deviceId: opt.deviceId, label: opt.label, icon: opt.icon})
    }
    return out
  }, [localOptions])

  const TriggerIcon = selected?.icon ?? CLOUD_ENV.icon
  const triggerLabel = selected?.label ?? CLOUD_ENV.label
  const activeDeviceId = selected?.kind === 'local' ? selected.deviceId : undefined

  return (
    <>
      <ActionMenu>
        <ActionMenu.Anchor>
          <Button
            leadingVisual={TriggerIcon}
            trailingAction={TriangleDownIcon}
            aria-label={`Environment: ${triggerLabel}`}
            disabled={disabled}
            variant="invisible"
            size="small"
            className={styles.selectorButton}
          >
            {triggerLabel}
          </Button>
        </ActionMenu.Anchor>
        {/* eslint-disable-next-line primer-react/no-system-props */}
        <ActionMenu.Overlay width="auto" overflow="auto">
          <ActionList selectionVariant="single">
            <ActionList.Group>
              <ActionList.GroupHeading>Environment</ActionList.GroupHeading>
              {cloudEnvironments.map(env => (
                <ActionList.Item key={env.id} selected={env.id === activeId} onSelect={() => handleSelect(env)}>
                  <ActionList.LeadingVisual>
                    <env.icon />
                  </ActionList.LeadingVisual>
                  {env.label}
                </ActionList.Item>
              ))}
              {ahpOptions.map(env => (
                <ActionList.Item key={env.id} selected={env.id === activeId} onSelect={() => handleSelect(env)}>
                  <ActionList.LeadingVisual>
                    <env.icon />
                  </ActionList.LeadingVisual>
                  {env.label}
                </ActionList.Item>
              ))}
              {uniqueDevices.length === 0 ? (
                <ActionList.Item disabled>
                  <ActionList.LeadingVisual>
                    <DeviceDesktopIcon />
                  </ActionList.LeadingVisual>
                  No connected devices
                </ActionList.Item>
              ) : (
                uniqueDevices.map(dev => (
                  <ActionList.Item
                    key={dev.deviceId}
                    selected={dev.deviceId === activeDeviceId}
                    onSelect={() => handleSelectDevice(dev.deviceId)}
                  >
                    <ActionList.LeadingVisual>
                      <dev.icon />
                    </ActionList.LeadingVisual>
                    {dev.label}
                  </ActionList.Item>
                ))
              )}
              <ActionList.Item onSelect={handleConnectDevice}>
                <ActionList.LeadingVisual>
                  <PlusIcon />
                </ActionList.LeadingVisual>
                Connect new device
              </ActionList.Item>
            </ActionList.Group>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {selected?.kind === 'local' && (
        <DirectoryPicker
          options={selectedDeviceOptions}
          activeOptionId={activeId}
          disabled={disabled}
          onSelect={handleSelect}
        />
      )}
      {isConnectDeviceDialogOpen && <ConnectDeviceDialog onClose={() => setIsConnectDeviceDialogOpen(false)} />}
      <div className="sr-only" role="status" aria-live="polite">
        {isSelectedUnavailable
          ? 'Selected environment is no longer available. Switched to the default environment.'
          : ''}
      </div>
    </>
  )
}
